import React from "react";
import { Issue } from "@/types/issues";
import {
  startOfDay,
  isPast,
  isSameDay,
  isWithinInterval,
  addDays,
} from "date-fns";
import {
  CircleDashed,
  Tag,
  UserCircle,
  TriangleDashed,
  Clock,
  ClockAlert,
} from "lucide-react";
import { NoPriorityIcon } from "@/types/priorities";
import { DueDateFilterValue } from "@/types/filterEnums";
import { STATE_KEY_TO_STATUS_ID } from "@/components/ProjectConfiguration/constants";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";

/**
 * Resolves an issue status to a valid status, falling back to Backlog for orphaned statuses
 * @param issueStatus - The status from the issue (can be null/undefined for orphaned statuses)
 * @param availableStatusNames - Array of available status names for validation
 * @returns The resolved status name (original if valid, Backlog if orphaned)
 */
export const resolveIssueStatus = (
  issueStatus: string | null | undefined,
  availableStatusNames: string[],
): string => {
  const status = issueStatus || KanbanDefaultStates.BACKLOG;

  if (!availableStatusNames || availableStatusNames.length === 0) {
    return KanbanDefaultStates.BACKLOG;
  }

  const statusLower = status.toLowerCase();

  const statusExists = availableStatusNames.some(
    (name) => name.toLowerCase() === statusLower,
  );

  return statusExists ? status : KanbanDefaultStates.BACKLOG;
};

const STATUS_ID_TO_STATE_KEY = Object.fromEntries(
  Object.entries(STATE_KEY_TO_STATUS_ID).map(([key, value]) => [value, key]),
);

const FILTER_ICON_COLOR = "#606064";

// Filter Options
export interface FilterOption {
  id: string;
  label: string;
  icon: React.ElementType;
  props?: any;
}

export const filterOptions: FilterOption[] = [
  {
    id: "status",
    label: "Status",
    icon: CircleDashed,
    props: { color: FILTER_ICON_COLOR },
  },
  {
    id: "label",
    label: "Label",
    icon: Tag,
    props: { color: FILTER_ICON_COLOR },
  },
  {
    id: "assignee",
    label: "Assignee",
    icon: UserCircle,
    props: { color: FILTER_ICON_COLOR },
  },
  {
    id: "estimate",
    label: "Estimate",
    icon: TriangleDashed,
    props: { color: FILTER_ICON_COLOR },
  },
  {
    id: "priority",
    label: "Priority",
    icon: NoPriorityIcon,
    props: { color: FILTER_ICON_COLOR },
  },
  {
    id: "dueDate",
    label: "Due Date",
    icon: Clock,
    props: { color: FILTER_ICON_COLOR },
  },
];

export const dueDateFilterOptions = [
  {
    id: "due-none",
    label: "No Due Date",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-overdue",
    label: "Overdue",
    icon: ClockAlert,
    className: "text-red-500",
  },
  {
    id: "due-today",
    label: "Due Today",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-this-week",
    label: "This Week",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-next-week",
    label: "Next Week",
    icon: Clock,
    className: "text-white",
  },
  {
    id: "due-later",
    label: "Later",
    icon: Clock,
    className: "text-white",
  },
];

// UI Components
export const FilterCount = ({ count }: { count: number }) => {
  return React.createElement(
    "span",
    { className: "text-xs text-muted-foreground ml-auto" },
    count,
  );
};

// Utility Functions
export const incrementCount = (counts: Record<string, number>, key: string) => {
  counts[key] = (counts[key] || 0) + 1;
};

// Filter Counting Functions
export const countByStatus = (
  issues: Issue[],
  availableStatuses?: string[],
) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    const finalStatus = resolveIssueStatus(
      issue.featurePrd?.status,
      availableStatuses || [],
    );

    incrementCount(counts, finalStatus);
  });

  return counts;
};

export const countByLabels = (issues: Issue[]) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    if (issue.canvas.labels && issue.canvas.labels.length > 0) {
      issue.canvas.labels.forEach((labelId) => {
        incrementCount(counts, labelId);
      });
    } else {
      incrementCount(counts, DueDateFilterValue.NONE);
    }
  });

  return counts;
};

export const countByAssignee = (issues: Issue[]) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    const assigneeId = issue.canvas.assigned_user_id || DueDateFilterValue.NONE;
    incrementCount(counts, assigneeId);
  });

  return counts;
};

export const countByEstimate = (issues: Issue[]) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    const estimate = issue.featurePrd?.estimate || 0;
    const key = estimate > 0 ? estimate.toString() : DueDateFilterValue.NONE;
    incrementCount(counts, key);
  });

  return counts;
};

export const countByPriority = (issues: Issue[]) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    const priority = issue.featurePrd?.priority || 0;
    incrementCount(counts, priority.toString());
  });

  return counts;
};

export const countByDueDate = (issues: Issue[]) => {
  const counts: Record<string, number> = {};

  issues.forEach((issue) => {
    const dueDate = issue.featurePrd?.due_date
      ? new Date(issue.featurePrd.due_date)
      : null;

    if (!dueDate) {
      incrementCount(counts, DueDateFilterValue.NONE);
      return;
    }

    const today = startOfDay(new Date());

    if (isPast(dueDate) && !isSameDay(dueDate, today)) {
      incrementCount(counts, DueDateFilterValue.OVERDUE);
    } else if (isSameDay(dueDate, today)) {
      incrementCount(counts, DueDateFilterValue.TODAY);
    } else if (
      isWithinInterval(dueDate, {
        start: today,
        end: addDays(today, 7),
      })
    ) {
      incrementCount(counts, DueDateFilterValue.THIS_WEEK);
    } else if (
      isWithinInterval(dueDate, {
        start: addDays(today, 7),
        end: addDays(today, 14),
      })
    ) {
      incrementCount(counts, DueDateFilterValue.NEXT_WEEK);
    } else if (dueDate > addDays(today, 14)) {
      incrementCount(counts, DueDateFilterValue.LATER);
    }
  });

  return counts;
};

export const calculateFilterCounts = (
  issues: Issue[],
  projectId: string,
  availableStatuses?: string[],
) => {
  if (!projectId || !Array.isArray(issues)) {
    return {
      statuses: {},
      labels: {},
      assignees: {},
      estimates: {},
      priorities: {},
      dueDates: {},
    };
  }

  const projectIssues = issues.filter(
    (issue) => issue?.canvas?.project_id === projectId,
  );

  return {
    statuses: countByStatus(projectIssues, availableStatuses),
    labels: countByLabels(projectIssues),
    assignees: countByAssignee(projectIssues),
    estimates: countByEstimate(projectIssues),
    priorities: countByPriority(projectIssues),
    dueDates: countByDueDate(projectIssues),
  };
};

// Filter State Management
export const createSelectedFiltersSet = (
  filterStatuses: string[],
  filterLabels: string[],
  filterUserIds: string[],
  filterEstimates: number[],
  filterPriorities: number[],
  filterDueDates: any,
) => {
  const filters = new Set<string>();

  // Add status filters
  filterStatuses?.forEach((status) => {
    filters.add(`status-${status}`);
  });

  // Add label filters
  filterLabels?.forEach((label) => {
    filters.add(`label-${label}`);
  });

  // Add user filters
  filterUserIds?.forEach((userId) => {
    filters.add(`assignee-${userId}`);
  });

  // Add estimate filters
  filterEstimates?.forEach((estimate) => {
    filters.add(`estimate-${estimate}`);
  });

  // Add priority filters
  filterPriorities?.forEach((priority) => {
    filters.add(`priority-${priority}`);
  });

  // Add due date filters
  if (filterDueDates) {
    if (filterDueDates.none) filters.add("due-none");
    if (filterDueDates.overdue) filters.add("due-overdue");
    if (filterDueDates.today) filters.add("due-today");
    if (filterDueDates.thisWeek) filters.add("due-this-week");
    if (filterDueDates.nextWeek) filters.add("due-next-week");
    if (filterDueDates.later) filters.add("due-later");
  }

  return filters;
};

export const updateFiltersFromSelected = (
  newSelectedFilters: Set<string>,
  setFilter: (projectId: string, filters: any) => void,
  projectId: string,
) => {
  const statuses: string[] = [];
  const labels: string[] = [];
  const userIds: string[] = [];
  const estimates: number[] = [];
  const priorities: number[] = [];
  const dueDates = {
    none: false,
    overdue: false,
    today: false,
    thisWeek: false,
    nextWeek: false,
    later: false,
  };

  newSelectedFilters.forEach((filter) => {
    if (filter.startsWith("status-")) {
      statuses.push(filter.replace("status-", ""));
    } else if (filter.startsWith("label-")) {
      labels.push(filter.replace("label-", ""));
    } else if (filter.startsWith("assignee-")) {
      userIds.push(filter.replace("assignee-", ""));
    } else if (filter.startsWith("estimate-")) {
      const value = filter.replace("estimate-", "");
      estimates.push(value === "none" ? 0 : parseInt(value, 10));
    } else if (filter.startsWith("priority-")) {
      const value = filter.replace("priority-", "");
      priorities.push(value === "none" ? 0 : parseInt(value, 10));
    } else if (filter === "due-none") {
      dueDates.none = true;
    } else if (filter === "due-overdue") {
      dueDates.overdue = true;
    } else if (filter === "due-today") {
      dueDates.today = true;
    } else if (filter === "due-this-week") {
      dueDates.thisWeek = true;
    } else if (filter === "due-next-week") {
      dueDates.nextWeek = true;
    } else if (filter === "due-later") {
      dueDates.later = true;
    }
  });

  if (!projectId) {
    return;
  }

  setFilter(projectId, {
    statuses,
    labels,
    userIds,
    estimates,
    priorities,
    dueDates,
  });
};

// UI Helper Functions
export const filterMenuItems = (items: JSX.Element[], searchQuery: string) => {
  if (!searchQuery) return items;

  const findTextContent = (children: React.ReactNode): string => {
    if (typeof children === "string") return children;
    if (!children) return "";

    if (Array.isArray(children)) {
      return children.map((child) => findTextContent(child)).join(" ");
    }

    if (React.isValidElement(children)) {
      return findTextContent(children.props.children);
    }

    return "";
  };

  return items.filter((item) => {
    const text = findTextContent(item.props.children).toLowerCase();
    return text.includes(searchQuery.toLowerCase());
  });
};

// Scrollbar styles
export const scrollbarStyles = `
    .filter-menu-scroll::-webkit-scrollbar {
        width: 8px;
    }
    .filter-menu-scroll::-webkit-scrollbar-track {
        background: transparent;
    }
    .filter-menu-scroll::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.3);
        border-radius: 4px;
    }
    .filter-menu-scroll::-webkit-scrollbar-thumb:hover {
        background-color: rgba(255, 255, 255, 0.5);
    }
`;
